import { makeAutoObservable, observable } from "mobx";
import Storage from "utils/localStorage";
import StoreCurrency from "store/currency";
import { get_symbol, get_leverSymbol } from "api/xt4/market";
import { get_fapiAccountOpen, get_dapiAccountOpen, get_fapiCoins, get_dapiCoins } from "api/xt4/futures";

export interface SymbolFilterProps {
  filter: "PRICE" | "QUANTITY";
  max: string;
  min: string;
  tickSize: string;
}

export interface SymbolProps extends ObjAny {
  symbol: string; //市场名称
  quantityPrecision: number; //卖方币市场精度
  pricePrecision: number; //买方币市场精度
  plates: number[]; //板块列表id
  state: string; //交易对状态[ONLINE=上线的;OFFLINE=下线的,DELISTED=退市]
  nextState: null | string; //下一个状态
  nextStateTime: null | number; //下一个状态时间
  displayLevel: string; //展示级别,[FULL=完全展示,SEARCH=搜索展示,DIRECT=直达展示,NONE=不展示]
  tradingEnabled: boolean; //是否启动交易(暂停交易)
  displayWeight: number; //展示权重，越大越靠前
  depthMergePrecision: number; //深度合并精度
  type: string; //类型 nft | normal
  filters?: SymbolFilterProps[]; //过滤器
}

export interface LeverSymbolProps extends ObjAny {
  // marketName: string; //市场名称
  symbol: string; //市场名称
  maxLeverage: number; //最大杠杆倍数
  maxLoanAmountBuy: string; //买方币种最大可借
  maxLoanAmountSell: string; //卖方币种最大可借
  liquidationRate: string; //爆仓风险率，市场风险率
  tipsRate: string; //提醒系数，预警风险率
  dailyInterestRate: string; //日利率
  sellCurrency: string; //卖方币
  buyCurrency: string; //买方币
}

interface StateProps {
  symbols: WithUndefined<SymbolProps[]>;
  leverConfigAry: WithUndefined<LeverSymbolProps[]>;
  isFuturesUsdtOpen: WithUndefined<boolean>;
  isFuturesCoinOpen: WithUndefined<boolean>;
  futuresUsdtTransferList: WithUndefined<string[]>;
  futuresCoinTransferList: WithUndefined<string[]>;
}

let TimeoutFun_loopGetMarketConfig, CancelFun_loopGetMarketConfig;

const market = makeAutoObservable(
  {
    symbols: undefined as StateProps["symbols"], //所有市场配置信息，数组
    leverConfigAry: undefined as StateProps["leverConfigAry"], //杠杆所有市场配置信息，数组
    isFuturesUsdtOpen: undefined as StateProps["isFuturesUsdtOpen"], //判断U本位合约是否开通
    isFuturesCoinOpen: undefined as StateProps["isFuturesCoinOpen"], //判断币本位合约是否开通
    futuresUsdtTransferList: undefined as StateProps["futuresUsdtTransferList"], //U本位合约可划转币种列表
    futuresCoinTransferList: undefined as StateProps["futuresCoinTransferList"], //币本位合约可划转币种列表

    get config(): WithUndefined<ObjT<SymbolProps>> {
      if (!this.symbols) return;
      const doc: ObjT<SymbolProps> = {};
      this.symbols.map((obj: any) => (doc[obj.symbol] = obj));
      return doc;
    }, //所有市场配置信息，对象
    get leverConfigObj(): WithUndefined<ObjT<LeverSymbolProps>> {
      if (!this.leverConfigAry) return;
      const doc: ObjT<LeverSymbolProps> = {};
      this.leverConfigAry.map((obj: any) => (doc[obj.symbol] = obj));
      return doc;
    }, //杠杆所有市场配置信息，对象
    //
    updateState(payload: Partial<StateProps>) {
      for (const va in payload) {
        this[va] = payload[va];
      }
    },
    formatName(symbol: string): string {
      // !symbol && (symbol = this.name);
      // return symbol.split("_").join("/").toUpperCase();

      return symbol
        .split("_")
        .map((coin) => StoreCurrency.getCurrencyDisplayName(coin))
        .join("/");
    }, //对市场名称进行格式化，btc_usdt -> BTC/USDT
    //
    loopGetMarketConfig() {
      clearTimeout(TimeoutFun_loopGetMarketConfig);
      try {
        CancelFun_loopGetMarketConfig && CancelFun_loopGetMarketConfig();
      } catch (e) {
        CancelFun_loopGetMarketConfig = null;
      }

      let { version, symbols } = Storage.get("symbols") || {};

      get_symbol({
        params: {
          version,
        },
        cancelFun: (c) => (CancelFun_loopGetMarketConfig = c),
      })
        .then((data) => {
          if (data.symbols && data.version !== version) {
            console.log("【getSymbol update】", data);
            symbols = data.symbols;
            // this.symbols = data.symbols;
            Storage.set("symbols", data);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.symbols = symbols;
          CancelFun_loopGetMarketConfig = null;
          clearTimeout(TimeoutFun_loopGetMarketConfig);
          TimeoutFun_loopGetMarketConfig = setTimeout(() => {
            this.loopGetMarketConfig();
          }, 3e3);
        });
    }, //轮询获取所有市场
    getLeverMarketConfig(finallyFun?: () => void) {
      get_leverSymbol()
        .then((data: StateProps["leverConfigAry"]) => {
          this.leverConfigAry = data;
        })
        .finally(finallyFun);
    }, //获取杠杆所有市场
    getFapiAccountOpen(finallyFun?: () => void) {
      if (this.isFuturesUsdtOpen) return finallyFun && finallyFun();
      get_fapiAccountOpen()
        .then((result) => {
          this.isFuturesUsdtOpen = result;
        })
        .finally(finallyFun);
    }, //判断U本位合约是否开通
    getDapiAccountOpen(finallyFun?: () => void) {
      if (this.isFuturesCoinOpen) return finallyFun && finallyFun();
      get_dapiAccountOpen()
        .then((result) => {
          this.isFuturesCoinOpen = result;
        })
        .finally(finallyFun);
    }, //判断币本位合约是否开通
    getFapiCoins(finallyFun?: () => void) {
      get_fapiCoins()
        .then((result) => {
          this.futuresUsdtTransferList = result;
        })
        .finally(finallyFun);
    }, //U本位合约可划转币种列表
    getDapiCoins(finallyFun?: () => void) {
      get_dapiCoins()
        .then((result) => {
          this.futuresCoinTransferList = result;
        })
        .finally(finallyFun);
    }, //币本位合约可划转币种列表
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default market;
